import { useState } from 'react'
import { toast } from 'react-toastify'
import { inviteAllTextMessageOptIn } from 'services'
import { Button } from 'stories/components'

interface IInviteSummary {
  toInvite: number
  alreadyOptedIn: number
  recentlyInvited: number
  skippedNoContact: number
  sent: number
  failed: number
}

/**
 * Invite every borrower who could be texted but has not opted in.
 *
 * Lives here, under the texting settings, rather than on a new Servicing screen: a new screen would
 * need its menu entry wired per tenant and would be invisible until someone did that. This is where
 * an admin already is when they turn the consent gate on, which is exactly when the existing book
 * needs inviting.
 *
 * Always previews first. It is a mass email to borrowers, so the count comes back before anything
 * is sent and sending is a second, deliberate click.
 */
export const TextMessageInviteAll = () => {
  const [isLoading, setLoading] = useState(false)
  const [summary, setSummary] = useState<IInviteSummary | null>(null)
  const [isSent, setSent] = useState(false)

  const onPreview = async () => {
    setLoading(true)
    setSent(false)
    try {
      const res: any = await inviteAllTextMessageOptIn(true)
      setSummary(res)
    } catch (error: any) {
      toast(error?.message || 'Could not work out who to invite.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onSend = async () => {
    setLoading(true)
    try {
      const res: any = await inviteAllTextMessageOptIn(false)
      setSummary(res)
      setSent(true)
      toast(`Sent ${res?.sent || 0} invitation${res?.sent === 1 ? '' : 's'}.`, { type: 'success' })
    } catch (error: any) {
      toast(error?.message || 'The invitations could not be sent.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="col-span-full border border-gray-200 rounded p-4 bg-gray-50/60">
      <div className="font-semibold mb-1">Invite borrowers to opt in</div>
      <p className="text-sm text-gray-500 mb-3">
        Emails every borrower who has a mobile number and an email address but has not opted in to text messages. The
        email carries a button that opts them in on the spot, with no sign-in, alongside the JOIN keyword and the
        portal checkbox. Anyone invited in the last 30 days is skipped. Nothing is sent until you confirm.
      </p>

      <div className="flex items-center gap-3">
        <Button color="gray" onClick={onPreview} loading={isLoading && !isSent}>
          {summary ? 'Recheck' : 'Check how many'}
        </Button>
        {summary && !isSent && summary.toInvite > 0 && (
          <Button onClick={onSend} loading={isLoading}>
            {`Send ${summary.toInvite} invitation${summary.toInvite === 1 ? '' : 's'}`}
          </Button>
        )}
      </div>

      {summary && (
        <ul className="text-sm text-gray-600 mt-3 list-disc pl-5 space-y-0.5">
          <li>
            <span className="font-semibold text-gray-800">{isSent ? summary.sent : summary.toInvite}</span>
            {isSent ? ' invitations sent' : ' borrowers would be invited'}
          </li>
          {isSent && summary.failed > 0 && (
            <li className="text-red-600">{summary.failed} could not be emailed. See the server log for which.</li>
          )}
          <li>{summary.alreadyOptedIn} have already opted in</li>
          <li>{summary.recentlyInvited} were invited in the last 30 days and were skipped</li>
          <li>{summary.skippedNoContact} have no usable mobile number</li>
        </ul>
      )}
    </div>
  )
}
